import axios from 'axios';

// IMPORTANT: keep the same build working on both dev + prod domains.
// CRA env vars are compile-time, so for the default we compute based on current hostname.
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

/**
 * Default API root when REACT_APP_API_URL is unset.
 * Development: `/api` — CRA proxies to Express :3001 (package.json "proxy").
 * Production: same-origin `/api` (Express or reverse proxy).
 */
function defaultAdminApiBaseUrl() {
  if (typeof window === 'undefined') return '';
  if (process.env.NODE_ENV === 'development') {
    return '/api';
  }
  return `${window.location.origin}/api`;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || defaultAdminApiBaseUrl();

// Main application URL:
// - prod admin (root.needfuel.net) -> app.needfuel.net
// - dev admin (addev.needfuel.net) -> dev.needfuel.net
const MAIN_APP_URL =
  process.env.REACT_APP_MAIN_APP_URL ||
  (hostname.startsWith('addev.needfuel.net') ? 'https://dev.needfuel.net' : 'https://app.needfuel.net');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timeout. Server is taking too long to respond.';
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      error.message = 'Cannot connect to server. Make sure the backend is running on port 3001.';
    } else if (error.response) {
      // Handle 401 unauthorized - redirect to login
      if (error.response.status === 401 || error.response.status === 403) {
        localStorage.removeItem('adminToken');
        // Check if we're already on login page (works with HashRouter)
        const currentPath = window.location.hash || window.location.pathname;
        if (!currentPath.includes('/login') && !currentPath.includes('/require-2fa')) {
          // Use hash for HashRouter
          window.location.hash = '#/login';
        }
      }
      error.message = error.response.data?.message || error.response.data?.error || error.message;
    }
    return Promise.reject(error);
  }
);

export const logsApi = {
  // Get logs with filters
  getLogs: async (params = {}) => {
    try {
      const response = await api.get('/logs', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch logs');
    }
  },

  // Get log statistics
  getStats: async (params = {}) => {
    try {
      const response = await api.get('/logs/stats', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch statistics');
    }
  },

  // Get filter options
  getFilters: async () => {
    try {
      const response = await api.get('/logs/filters');
      return response.data;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch filter options');
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      throw new Error(error.message || 'Health check failed');
    }
  },

  // Get active users from main application
  getActiveUsers: async (params = {}) => {
    try {
      // Call main application API instead of admin dashboard API
      const mainAppApi = axios.create({
        baseURL: MAIN_APP_URL,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const response = await mainAppApi.get('/api/users/active', { params });
      return response.data;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Main application is taking too long to respond.');
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        throw new Error(`Cannot connect to main application at ${MAIN_APP_URL}. Make sure the northstar-fuel application is running and accessible.`);
      }
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch active users from main application');
    }
  },

  // Export logs to CSV
  exportToCSV: async (params = {}) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/logs/export-csv`, {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `logs-export-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      return { success: true };
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to export logs to CSV');
    }
  },

  // Delete all logs
  deleteAllLogs: async (params = {}) => {
    try {
      const response = await api.delete('/logs', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete logs');
    }
  },
};

/** QuikQ station lookup (admin server proxies QuikQ; requires env credentials on server) */
export const stationsApi = {
  getConfigStatus: async () => {
    const response = await api.get('/stations/config-status');
    return response.data;
  },
  search: async (body) => {
    const response = await api.post('/stations/search', body, { timeout: 90000 });
    return response.data;
  },
};

/** Global in-app message shown to Need Fuel users when they use the app (maintenance etc.) */
export const announcementApi = {
  get: async () => {
    const response = await api.get('/user-announcement');
    return response.data;
  },
  save: async (body) => {
    const response = await api.put('/user-announcement', body);
    return response.data;
  },
};

/** Casey's CSV → Mongo staging + stationCode mappings */
export const pricingApi = {
  uploadCaseysCsv: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/pricing/caseys/upload', formData, {
      timeout: 120000,
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        },
      ],
    });
    return response.data;
  },
  listCaseysBatches: async (params = {}) => {
    const response = await api.get('/pricing/caseys/batches', { params });
    return response.data;
  },
  listBatchRows: async (batchId, params = {}) => {
    const response = await api.get(`/pricing/caseys/batches/${batchId}/rows`, { params });
    return response.data;
  },
  mapStagingRowStation: async (rowId, stationCode) => {
    const response = await api.patch(`/pricing/staging/${rowId}/station`, { stationCode });
    return response.data;
  },
  uploadCombinedFuelCsv: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/pricing/combined/upload', formData, {
      timeout: 600000,
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        },
      ],
    });
    return response.data;
  },
  listCombinedFuelBatches: async (params = {}) => {
    const response = await api.get('/pricing/combined/batches', { params, timeout: 60000 });
    return response.data;
  },
  listCombinedFuelBatchRows: async (batchId, params = {}) => {
    const response = await api.get(`/pricing/combined/batches/${batchId}/rows`, { params, timeout: 60000 });
    return response.data;
  },
  uploadMasterLocationCsv: async (file, provider) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/master-locations/upload', formData, {
      params: provider ? { provider } : undefined,
      timeout: 120000,
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        },
      ],
    });
    return response.data;
  },
  getMasterLocationSummary: async () => {
    const response = await api.get('/master-locations/summary', { timeout: 60000 });
    return response.data;
  },
  listMasterLocations: async (params = {}) => {
    const response = await api.get('/master-locations', { params, timeout: 60000 });
    return response.data;
  },
  upsertMasterLocation: async (body) => {
    const response = await api.post('/master-locations', body, { timeout: 60000 });
    return response.data;
  },
  getMissingFuelPriceDataReport: async (params = {}) => {
    const response = await api.get('/master-locations/missing-data-report', { params, timeout: 120000 });
    return response.data;
  },
  enrichFuelPricesFromMasterLocations: async (body = {}) => {
    const response = await api.post('/master-locations/enrich-fuel-prices', body, { timeout: 300000 });
    return response.data;
  },
  listMappings: async (params = {}) => {
    const response = await api.get('/pricing/mappings', { params });
    return response.data;
  },
  // Price overrides for station-level pricing adjustments
  getPriceOverridesSummary: async () => {
    const response = await api.get('/price-overrides/summary');
    return response.data;
  },
  listPriceOverrideProducts: async () => {
    const response = await api.get('/price-overrides/products');
    return response.data;
  },
  listPriceOverrides: async (params = {}) => {
    const response = await api.get('/price-overrides', { params });
    return response.data;
  },
  /**
   * Same POST body + URL as Stations (`/stations/search`). Uses enrichForPriceOverrides so repricing
   * rows are attached — avoids 404 when older proxies/servers lack `/price-overrides/quikq-search`.
   */
  searchQuikqForPriceOverride: async (body) => {
    const response = await api.post(
      '/stations/search',
      { ...body, enrichForPriceOverrides: true },
      { timeout: 90000 }
    );
    return response.data;
  },
  /** Legacy structured search (not used when mirroring Stations Postman flow) */
  searchStationsForPriceOverride: async (body) => {
    const response = await api.post('/price-overrides/station-search', body, { timeout: 90000 });
    return response.data;
  },
  getStationOverrides: async (stationCode) => {
    const response = await api.get(`/price-overrides/station/${stationCode}`);
    return response.data;
  },
  createPriceOverride: async (override) => {
    const response = await api.post('/price-overrides', override);
    return response.data;
  },
  updatePriceOverride: async (stationCode, prodCode, override) => {
    const response = await api.put(`/price-overrides/${stationCode}/${prodCode}`, override);
    return response.data;
  },
  deletePriceOverride: async (stationCode, prodCode) => {
    const response = await api.delete(`/price-overrides/${stationCode}/${prodCode}`);
    return response.data;
  },
  deactivatePriceOverride: async (stationCode, prodCode) => {
    const response = await api.patch(`/price-overrides/${stationCode}/${prodCode}/deactivate`);
    return response.data;
  },
  reactivatePriceOverride: async (stationCode, prodCode) => {
    const response = await api.patch(`/price-overrides/${stationCode}/${prodCode}/reactivate`);
    return response.data;
  },
  // Get list of all stations for autocomplete
  getStationsList: async (params = {}) => {
    const response = await api.get('/stations/list', { params });
    return response.data;
  },
};

export default api;

